"use client";
import {
  AgendaDay,
  ISchedule,
  IWorkingLocationPresenceType,
  Office,
  ResourceType,
  WL_PRESENCE_OFFICE,
  WL_PRESENCE_OOO,
  WL_PRESENCE_REMOTE
} from "@gettactic/api";
import { getFirstMondayBefore, parseApiDate } from "@gettactic/helpers";
import {
  isWorkingLocationsWithOOOFeaturesEnabled,
  orgRequiresReservationAfterPickingAnOffice
} from "@gettactic/helpers/src/featureFlags";
import {
  collectSchedulePages,
  SCHEDULE_PAGE_SIZE
} from "@gettactic/helpers/src/schedules/collectSchedulePages";
import {
  defaultWeekDays,
  MS_1_MINUTE,
  useAuthenticated,
  useAvailableDays,
  useDedicatedResourceOf,
  useMyAgenda,
  useUserDedicatedExceptions,
  useWeekStartsOn
} from "@gettactic/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { addDays, format, startOfWeek } from "date-fns";
import { uniq } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  type ReleaseDeskSelection,
  useReleaseDedicatedDesk
} from "@/features/reserve/hooks/useReleaseDedicatedDesk";
import { api } from "@/lib/api/api";
import { useUserOfficesAvailability } from "@/lib/hooks/userOfficesAvailability";
import { dateParseISO, isWorkingLocationOffice } from "@/lib/utils";
import { createDayChangeGuard } from "../lib/dayChangeGuard";
import {
  deskActionAfterChange,
  type LocationChangeOutcome
} from "../lib/deskAfterLocationChange";
import {
  type DeskToRelease,
  deskToRelease,
  filterReservationsElsewhere,
  isReleasedOn,
  officeToday
} from "../lib/reservationsElsewhere";

interface OptimisticUpdate {
  location: IWorkingLocationPresenceType;
  officeId?: string;
}

/**
 * A working location change held back because the day has reservations
 * somewhere else, or a dedicated desk being left behind: the person decides
 * what to cancel or release before it goes through.
 */
export interface PendingLocationChange {
  /** `yyyy-MM-dd` */
  date: string;
  newLocationId: IWorkingLocationPresenceType;
  /** The reservations that day in a different office than the new location. */
  reservations: ISchedule[];
  /** True when there were more reservations than could be listed. */
  isTruncated: boolean;
  /** The person's dedicated desk, when the change leaves its office for the day. */
  desk: DeskToRelease | null;
  /**
   * Applies the change, after the reservations were cancelled or kept, and
   * says what became of it. A day needing a leave request only opens one and
   * stays as it was until that is submitted and approved.
   *
   * `selection` is what the person chose for their dedicated desk. The desk
   * is freed in here rather than by the caller afterwards, so that it happens
   * while the day is still held: a release running after the hold was given
   * back could be overtaken by a second pick putting them back at the office.
   */
  proceed: (selection: ReleaseDeskSelection) => Promise<LocationChangeOutcome>;
}

export function useDashboardLocation({
  onDateChange
}: {
  onDateChange?: (date: string) => void;
} = {}) {
  const [optimisticUpdates, setOptimisticUpdates] = useState<
    Record<string, OptimisticUpdate>
  >({});
  const [loadingUpdates, setLoadingUpdates] = useState<Record<string, boolean>>(
    {}
  );
  /**
   * Which days have a change being worked on, so a second pick cannot race the
   * first. Held in a ref because the callbacks that hand a day back run long
   * after the call that took it returned.
   */
  const guard = useRef(createDayChangeGuard()).current;
  const { release } = useReleaseDedicatedDesk();

  const [oooRequest, setOooRequest] = useState<{
    date: string;
    type: "ooo" | "remote";
  } | null>(null);
  const [pendingLocationChange, setPendingLocationChange] =
    useState<PendingLocationChange | null>(null);
  const {
    userContext: { currentOffice, authenticatedUser, deviceTz }
  } = useAuthenticated();
  const [reserveModalData, setReserveModalData] = useState<{
    start: Date;
    resourceType: ResourceType;
    office: Office | undefined;
  } | null>(null);
  const [weekDate, setWeekDate] = useState<string>(() =>
    format(new Date(), "yyyy-MM-dd")
  );

  useEffect(() => {
    // for example, to keep updated date in a dashboard-wide filter
    onDateChange?.(weekDate);
  }, [weekDate]);

  const weekStartsOn = useWeekStartsOn();
  const parsedDate = dateParseISO(weekDate);
  const firstMondayBefore = startOfWeek(parsedDate, { weekStartsOn });
  const firstMondayBeforeStr = format(firstMondayBefore, "yyyy-MM-dd");
  const endDateForWeekStr = format(addDays(firstMondayBefore, 7), "yyyy-MM-dd");

  const { data: availableDays } = useAvailableDays(currentOffice, {
    enabled: !!currentOffice.id,
    staleTime: MS_1_MINUTE
  });

  const queryClient = useQueryClient();

  const scheduleMeData = useMyAgenda({
    user_tz: deviceTz,
    from: getFirstMondayBefore(weekDate, weekStartsOn)
  });

  // The person's dedicated desk and the days it is released: the agenda
  // shows the desk on days at its office, and leaving that office for a day
  // offers to release it.
  const userId = authenticatedUser.user?.id;
  const {
    data: dedicatedDesk,
    isPending: isDeskPending,
    isError: didDeskFail
  } = useDedicatedResourceOf(userId);
  const {
    data: deskReleases,
    isPending: areDeskReleasesPending,
    isError: didDeskReleasesFail
  } = useUserDedicatedExceptions(
    dedicatedDesk ? userId : undefined,
    firstMondayBeforeStr
  );
  // The desk itself has to settle before its releases mean anything: while it
  // is still on its way, "this person has no desk" and "nobody has looked yet"
  // are the same missing value, and reading it as the first shows the reserve
  // prompt to someone who has a desk. A query that is off (no signed-in user)
  // stays pending for good, so that counts as settled.
  const isDeskLoading = !!userId && isDeskPending && !didDeskFail;
  // Until the releases are in, nothing is known about them: treating that as
  // "not released" would show the desk as the person's own on a day they gave
  // it away, and offer to release a day that already is. A request that
  // failed is not an answer either, so this asks whether they arrived rather
  // than whether the wait is over.
  const areDeskReleasesKnown =
    !isDeskLoading && (!dedicatedDesk || deskReleases !== undefined);
  // Still waiting, as opposed to having given up: the agenda holds both the
  // desk card and the reserve prompt back while waiting, and falls back to
  // the prompt once the answer is not coming.
  const areDeskReleasesLoading =
    isDeskLoading ||
    (!!dedicatedDesk && areDeskReleasesPending && !didDeskReleasesFail);
  const isDeskReleasedOn = useCallback(
    (date: string) => isReleasedOn(deskReleases ?? [], date),
    [deskReleases]
  );

  // Compute office IDs and dates for availability check
  const { officeIds, dates } = useMemo(() => {
    if (!scheduleMeData.data) {
      return { officeIds: [], dates: [] };
    }

    const officeIds: string[] = [];
    const dates: string[] = [];

    for (const date in scheduleMeData.data.agenda) {
      const day = scheduleMeData.data.agenda[date];
      // Only fetch office availability for days where the user is in the office_id and has no reservations
      if (day.office_id && !day.schedules.length) {
        officeIds.push(day.office_id);
        dates.push(date);
      }
    }

    return { officeIds: uniq(officeIds), dates: uniq(dates) };
  }, [scheduleMeData.data]);

  const officeAvailabilityQuery = useUserOfficesAvailability({
    officeIds,
    dates
  });
  const officeAvailabilityData = officeAvailabilityQuery?.data ?? null;
  const isLoadingOfficeAvailability =
    officeAvailabilityQuery?.isLoading ?? false;

  const nextWeekDate = scheduleMeData.data?.next_week_date;
  const previousWeekDate = scheduleMeData.data?.previous_week_date;

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["schedules"]
    });
    queryClient.invalidateQueries({
      queryKey: ["myAgenda"]
    });
    queryClient.invalidateQueries({
      queryKey: ["attendanceRequests"]
    });
    queryClient.invalidateQueries({
      queryKey: ["leavePolicies"]
    });
    // Also invalidate hours.rules to ensure availableDays stays fresh
    queryClient.invalidateQueries({
      queryKey: ["hours.rules"]
    });
  }, [queryClient]);

  const openReserveModal = (date: string, office: Office | undefined) => {
    setReserveModalData({
      start: parseApiDate(date),
      resourceType: "desk",
      office: office ?? undefined
    });
  };

  const updateWorkingLocation = async (
    newWorkingLocationId: IWorkingLocationPresenceType,
    targetDate: string, // YYYY-MM-DD
    dayNumber: number, // 0 - sunday, 6 - saturday
    agendaBeforeUpdate: AgendaDay | null
  ) => {
    if (guard.isBusy(targetDate)) {
      return;
    }
    guard.begin(targetDate);
    try {
      await runWorkingLocationChange(
        newWorkingLocationId,
        targetDate,
        dayNumber,
        agendaBeforeUpdate
      );
    } finally {
      guard.end(targetDate);
    }
  };

  /** Closing the prompt without answering leaves the day as it was. */
  const dismissPendingLocationChange = useCallback(() => {
    setPendingLocationChange(null);
    guard.dismissPrompt();
  }, [guard]);

  const runWorkingLocationChange = async (
    newWorkingLocationId: IWorkingLocationPresenceType,
    targetDate: string, // YYYY-MM-DD
    dayNumber: number, // 0 - sunday, 6 - saturday
    agendaBeforeUpdate: AgendaDay | null
  ) => {
    const isOffice = isWorkingLocationOffice(newWorkingLocationId);
    const remotePolicy = agendaBeforeUpdate?.policy;

    const location = isOffice
      ? {
          office_id: newWorkingLocationId,
          location: WL_PRESENCE_OFFICE as IWorkingLocationPresenceType
        }
      : {
          location: newWorkingLocationId
        };

    const isChangingOffice =
      newWorkingLocationId !== agendaBeforeUpdate?.location &&
      newWorkingLocationId !== agendaBeforeUpdate?.office_id;

    // Improve how we store optimistic updates to ensure we're handling all location types correctly
    const optimisticUpdate = {
      location: newWorkingLocationId,
      ...(isOffice && { officeId: newWorkingLocationId })
    };
    const startOptimisticUpdate = () => {
      setOptimisticUpdates((prev) => ({
        ...prev,
        [targetDate]: optimisticUpdate
      }));

      startLoadingState();
    };

    const startLoadingState = () => {
      setLoadingUpdates((prev) => ({
        ...prev,
        [targetDate]: true
      }));
    };

    // Function to clear loading state for this date
    const clearLoadingState = () => {
      setLoadingUpdates((prev) => {
        const newState = { ...prev };
        delete newState[targetDate];
        return newState;
      });
    };

    // Function to remove optimistic update in case of error
    const clearOptimisticUpdate = () => {
      setOptimisticUpdates((prev) => {
        const newState = { ...prev };
        delete newState[targetDate];
        return newState;
      });
    };

    try {
      // Answers whether the day's working location was actually written.
      // Some days only open a leave request instead, and stay as they were
      // until that is submitted and approved, so a caller acting on the
      // change having landed has to be able to tell the difference.
      const performUpdate = async (): Promise<boolean> => {
        try {
          let committed = true;
          let hasPolicies = false;

          if (
            newWorkingLocationId === WL_PRESENCE_OOO &&
            isWorkingLocationsWithOOOFeaturesEnabled(authenticatedUser)
          ) {
            const res = await api.client.leave.allApplicablePolicies({
              tz: deviceTz,
              start: firstMondayBeforeStr,
              end: endDateForWeekStr
            });
            const allPolicies = res.filter((x) => !!x && !!x.policy);
            hasPolicies =
              (allPolicies ?? []).filter(
                (x) => x.policy.policy_type !== WL_PRESENCE_REMOTE
              ).length > 0;
          }

          if (
            newWorkingLocationId === WL_PRESENCE_OOO &&
            hasPolicies &&
            isWorkingLocationsWithOOOFeaturesEnabled(authenticatedUser)
          ) {
            if (hasPolicies) {
              setOooRequest({ date: targetDate, type: "ooo" });
              clearOptimisticUpdate();
              committed = false;
            }
          } else if (
            newWorkingLocationId === WL_PRESENCE_REMOTE &&
            remotePolicy &&
            remotePolicy.status === "pending"
          ) {
            setOooRequest({ date: targetDate, type: "remote" });
            clearOptimisticUpdate();
            committed = false;
          } else if (
            newWorkingLocationId === WL_PRESENCE_REMOTE &&
            remotePolicy &&
            remotePolicy.status === "approved"
          ) {
            // we should silently send the Remote Request because is not approvable
            await api.client.leave.createRequest({
              policy_type: remotePolicy.type,
              reason: "",
              starts: `${targetDate}T00:00:00.000Z`,
              ends: `${targetDate}T23:59:59.000Z`,
              time_zone_id: deviceTz
            });
          } else {
            await api.client.schedules.updateWorkingLocationDay(
              location,
              dayNumber,
              targetDate
            );
          }
          await invalidateQueries();

          if (
            isOffice &&
            orgRequiresReservationAfterPickingAnOffice(authenticatedUser) &&
            isChangingOffice
          ) {
            const targetOffice = (
              authenticatedUser.offices?.offices ?? []
            ).find((x) => x.id === newWorkingLocationId);
            openReserveModal(targetDate, targetOffice);
          }

          return committed;
        } catch (e) {
          clearOptimisticUpdate();
          toast.error("Error updating working location");
          throw e; // Re-throw to be caught by the outer try-catch
        }
      };

      if (isChangingOffice) {
        // The day's reservations that are not where the person will be: a
        // desk kept in the old office, anything at all once the day is
        // remote. Those are asked about before the change goes through;
        // reservations in the office picked are left alone.
        let elsewhere: { reservations: ISchedule[]; isTruncated: boolean };
        // The day is already busy from here: the fetch below takes a moment,
        // and without this the picker looks idle while it runs.
        startLoadingState();
        try {
          const { schedules, isTruncated } = await collectSchedulePages(
            (cursor) =>
              api.client.schedules.mine({
                start: targetDate,
                end: targetDate,
                tz: deviceTz,
                limit: SCHEDULE_PAGE_SIZE,
                cursor
              })
          );
          elsewhere = {
            reservations: filterReservationsElsewhere(
              schedules,
              newWorkingLocationId
            ),
            isTruncated
          };
        } catch {
          toast.error(
            "Couldn't check your reservations for that day. Your working location was not changed."
          );
          return;
        }

        // The desk's released days decide whether it is already free on the
        // day, and the query holding them is keyed on the week being looked
        // at: it is refetching on every week change, which is exactly when
        // someone marks future days remote. Waiting on it would drop the
        // offer in that window and quietly leave the desk taken, so the day
        // is asked for directly, as its reservations were just above.
        let releases = deskReleases ?? [];
        let areReleasesKnown = areDeskReleasesKnown;
        if (dedicatedDesk && userId && !areReleasesKnown) {
          try {
            const response =
              await api.client.organizations.userDedicatedExceptionsGet(
                userId,
                targetDate
              );
            releases = response.result ?? [];
            areReleasesKnown = true;
          } catch {
            // Still nothing known about them. Better no offer than one that
            // would release a day already released.
            areReleasesKnown = false;
          }
        }

        // Leaving the dedicated desk's office for the day: offer to release
        // it, reading "today" on the desk's own office calendar rather than
        // the viewer's.
        const desk = areReleasesKnown
          ? deskToRelease(
              dedicatedDesk,
              agendaBeforeUpdate?.location,
              agendaBeforeUpdate?.office_id,
              newWorkingLocationId,
              releases,
              targetDate,
              officeToday(
                dedicatedDesk?.office_id
                  ? authenticatedUser.offices?.byId[dedicatedDesk.office_id]
                      ?.time_zone
                  : undefined
              )
            )
          : null;

        if (
          elsewhere.reservations.length > 0 ||
          elsewhere.isTruncated ||
          desk
        ) {
          guard.prompt(targetDate);
          setPendingLocationChange({
            date: targetDate,
            newLocationId: newWorkingLocationId,
            reservations: elsewhere.reservations,
            isTruncated: elsewhere.isTruncated,
            desk,
            proceed: async (selection) => {
              // Only if this is still the prompt on screen. A cancellation
              // confirmed on this day can land after another day's prompt has
              // taken over, and clearing it outright would take that prompt
              // away before anybody answered it.
              setPendingLocationChange((current) =>
                current?.date === targetDate ? null : current
              );
              // The answer turns the prompt back into work, so the day is
              // taken in hand again before the prompt lets go of it. Until
              // here it was held by the prompt alone, and another day's
              // prompt taking the screen would free it mid-write.
              guard.begin(targetDate);
              // Optimistic only once the person has answered
              startOptimisticUpdate();
              try {
                let outcome: LocationChangeOutcome;
                try {
                  const committed = await performUpdate();
                  await queryClient.invalidateQueries({
                    queryKey: ["myAgenda"],
                    exact: false
                  });
                  outcome = committed ? "changed" : "awaiting_approval";
                } catch {
                  clearOptimisticUpdate();
                  // performUpdate has already said so to the person; there
                  // is nothing more to say for the day.
                  outcome = "failed";
                }
                // Freeing the desk is part of answering for the day, so it
                // happens inside the day's hold. Left to the caller it ran
                // after the hold was given back, and a second pick could put
                // the person back at the office on a day being given away.
                const action = deskActionAfterChange(
                  outcome,
                  !!desk && selection.choice !== "keep"
                );
                if (desk && action === "release") {
                  await release({
                    deskId: desk.id,
                    deskName: desk.name,
                    // The agenda's day is keyed on the viewer's zone; the
                    // release holds it to the desk office's calendar.
                    officeId: desk.officeId,
                    startsOn: targetDate,
                    endsOn: targetDate,
                    selection,
                    reason: "Working elsewhere"
                  });
                }
                if (desk && action === "explain") {
                  // Said out loud, because nothing picks the release up
                  // later: the day changes when the request is approved, and
                  // the desk does not.
                  toast(
                    `${desk.name} is still yours — the day needs approving first. Release it once that is through.`
                  );
                }
                return outcome;
              } finally {
                guard.end(targetDate);
                guard.answered(targetDate);
                setTimeout(clearLoadingState, 300);
              }
            }
          });
          return;
        }
      }

      // Nothing in the way: the change goes straight through
      startOptimisticUpdate();
      await performUpdate();
      await queryClient.invalidateQueries({
        queryKey: ["myAgenda"],
        exact: false
      });
    } catch {
      // Any uncaught errors will be handled here
      clearOptimisticUpdate();
    } finally {
      // Always ensure loading state is cleared with a slight delay
      // This helps prevent flashes during fast operations
      setTimeout(clearLoadingState, 300);
    }
  };

  const nextWeek = useCallback(() => {
    if (!scheduleMeData?.data) {
      return;
    }
    setWeekDate(nextWeekDate ?? "");

    // do this on data update
  }, [scheduleMeData?.data]);

  const prevWeek = useCallback(() => {
    if (!scheduleMeData?.data) {
      return;
    }
    setWeekDate(previousWeekDate ?? "");

    // do this on data update
  }, [scheduleMeData?.data]);

  const getLocationForDate = useCallback(
    (date: string, currentLocation: AgendaDay | null) => {
      const optimistic = optimisticUpdates[date];

      if (optimistic) {
        // We need to use type assertion to handle the complex type conversion
        const mergedLocation = {
          ...(currentLocation || {}),
          location: optimistic.location,
          ...(optimistic.officeId ? { office_id: optimistic.officeId } : {})
        } as AgendaDay;

        return mergedLocation;
      }

      return currentLocation;
    },
    [optimisticUpdates]
  );

  const isLocationLoading = useCallback(
    (date: string) => {
      const dateKey = date.split("T")[0]; // Get just the date part
      return !!loadingUpdates[dateKey];
    },
    [loadingUpdates]
  );

  return {
    authenticatedUser,
    weekDate,
    setWeekDate,
    scheduleMeData,
    officeAvailabilityData,
    availableDays: availableDays || defaultWeekDays,
    updateWorkingLocation,
    openReserveModal,
    reserveModalData,
    oooRequest,
    setOooRequest,
    pendingLocationChange,
    dismissPendingLocationChange,
    setReserveModalData,
    nextWeek,
    prevWeek,

    invalidateQueries,

    getLocationForDate,
    isLocationLoading,
    isLoadingOfficeAvailability,
    optimisticUpdates,

    dedicatedDesk: dedicatedDesk ?? null,
    isDeskReleasedOn,
    areDeskReleasesKnown,
    areDeskReleasesLoading
  };
}
